import type { ArrivalIcon } from '../types';
import { ArrivalOptionIcon } from './ArrivalOptionIcon';

export interface ArrivalOptionButtonProps {
  readonly title: string;
  readonly subtitle: string;
  /** Which glyph the frame draws in this option's circle. See `ArrivalOptionIcon` for the mapping. */
  readonly icon: ArrivalIcon;
  /**
   * Whether this row is drawn in the tinted "primary" treatment: a `--primary-muted` fill with a solid
   * `--primary` icon disc, where every other row is `--card` with a tinted disc.
   *
   * Which options get it is the caller's to say — `ArrivalOption.recommended` carries the two, and the two
   * sources that name them. This component only draws it, and it never reads anyone's record.
   */
  readonly recommended?: boolean;
  readonly onClick: () => void;
}

/**
 * One row of the arrival-reason menu [Figma · `12 · What brings you in today?`, all three viewports].
 *
 * **The row IS the control.** Circled icon · title · subtitle · chevron, tappable end to end — one `<button>`
 * and nothing focusable inside it.
 *
 * ⚠️ **It was a card containing a full-width teal button between 2026-08-24 and this change, and that was
 * not a design the file contains.** The card carried seven verbs — `See my assignment`, `See open jobs`,
 * `Finish my I-9`, `Get the details`, `Get a pay card`, `See my application`, `Talk to someone` — and
 * **none of the seven appears anywhere in the design file, at any of the three widths.** The frame draws no
 * such element, so § 8 leaves nothing to derive them from and they were written here: seven strings for a
 * stranger to read, invented by the build. (KID-5 § Round-2 records the candidate-facing wording as still
 * owed from ISS, and decision 0040 rules that internal verbiage never reaches a candidate-facing screen —
 * ⚠️ 0040 does NOT reserve copy authorship to ISS, which is how it is often paraphrased. The § 8 argument
 * is the one that carries this deletion, and it carries it on its own.) The rows also stood at 448 × 144
 * against a drawn 448 × 74, which made the option list 1120px where the frame's is 547. Both are gone: the
 * title is the affordance, and the chevron says the row goes somewhere.
 *
 * **Measured from the frame, not chosen.** 16px padding, a 12px gap, a 40px disc holding a 20px glyph, a
 * 330px text column at 1440 and 820 (209 at 375) and a 20px chevron box: 16 + 40 + 12 + 330 + 12 + 20 + 16
 * = 446, plus the 1px border on each side = the 448 the frame reports. All three children are CENTRED on the
 * row's cross axis, which is what makes the taller rows measure as drawn — the drug test at 87 on the wide
 * frames, and 106 / 157 at 375 — where the copy wraps.
 *
 * **Nothing pins a row height.** 16 + max(40, the text block) + 16 + 2 reproduces the frame's height at
 * every width where the copy wraps to the same number of lines the frame draws, which is what lets the
 * Spanish column — longer almost everywhere — wrap without a hardcoded number going stale.
 *
 * ⚠️ **Two rows do NOT reach the frame's height, and it is the wrap, not the box.** The I-9 option
 * measures 121 against a drawn 104 at 1440 / 820, and `See what I applied for` 87 against 74 at 375: in
 * both, Inter takes one line more than the SF Pro Display the frame is drawn in. That is the OQ-52 class
 * `design-qa/config.json` already carries twelve entries for. The per-width numbers are in
 * `tasks/_shared/evidence/TASK-023/notes.md`; do not read the arithmetic above as a claim that every row
 * matches.
 *
 * ⚠️ **The line heights stay PINNED, and that is not leftover caution** — it is the same trap the 144px card
 * documented, with the frame's new numbers. `leading-[19px]` and `leading-[17px]` are the frame's 19.09 and
 * 16.71; left to `normal` they become the FONT's own leading, and the frame is drawn in SF Pro Display while
 * this app ships Inter (OQ-52 is still open on the substitution). The old row lost 2px per option that way
 * and had drifted 10px by the sixth.
 *
 * ⚠️ **Two departures from the convention skill, both written into
 * `design-qa/known-departures.md` with a revisit trigger** — not left in this file, where only a reader of
 * this file would find them (§ 9's own instruction).
 * · **§ 9's 3:1 boundary bar.** The frame draws the row's border in `--border-subtle` (#d7d6d5): 1.45:1 on
 *   `--card`, 1.41:1 on the tinted row. It bounds a control now that the row is the button, and it is below
 *   the bar. It carries nothing on its own — the title, the disc, the chevron and the 3px `--primary` focus
 *   ring each announce the control — and it is the frame's own value (§ 8). Raised to design, not quietly
 *   darkened. `src/index.css` carried the sentence "never a control boundary" about this token until
 *   2026-09-10; this change is what falsified it, and it was corrected there in the same commit.
 * · **The chevron is FILLED in the frame** (a 6 × 10 path in #757372) and is stroked here, for the reason
 *   `BackLink` gives: a colour baked into a path cannot follow the control it belongs to, and `currentColor`
 *   can. The drawn extents are identical.
 *
 * The § 9 touch floor is met with room to spare — the smallest row is 74px tall and the whole of it is the
 * target, where the retired card gave a finger a 52px button inside a 144px box.
 */
export function ArrivalOptionButton({
  title,
  subtitle,
  icon,
  recommended = false,
  onClick,
}: ArrivalOptionButtonProps) {
  return (
    <button
      className={`kiosk-focusable flex w-full items-center gap-3 rounded-[var(--radius-sm)] border border-[var(--border-subtle)] p-4 text-left ${
        recommended ? 'bg-[var(--primary-muted)]' : 'bg-[var(--card)]'
      }`}
      onClick={onClick}
      type="button"
    >
      <span
        className={`flex size-10 shrink-0 items-center justify-center rounded-full ${
          recommended
            ? 'bg-[var(--primary)] text-[var(--primary-foreground)]'
            : 'bg-[var(--primary-muted)] text-[var(--primary)]'
        }`}
      >
        <ArrivalOptionIcon icon={icon} />
      </span>

      {/*
        The frame's `Text` frame: two stacked lines with NO gap between them — 19 + 17 = the 36 it reports.
        `min-w-0` is what lets the column shrink to the drawn 330 (209 at 375) instead of being pushed wide
        by an unbreakable title; without it a flex child refuses to go below its content width.
      */}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-base font-bold leading-[19px] text-[var(--foreground)]">{title}</span>
        <span className="text-sm leading-[17px] text-[var(--muted-foreground)]">{subtitle}</span>
      </span>

      {/*
        The 20px box and the 6 × 10 glyph inside it are kept SEPARATE, because the frame nests them that way
        and because drawing the mark at the box's size is a mistake this repository has now made three times
        (the select's chevron, then Back's). Centred in the box: (20 − 6) / 2 and (20 − 10) / 2 are the 7 and
        5 the frame reports.
      */}
      <span className="flex size-5 shrink-0 items-center justify-center text-[var(--subtle-foreground)]">
        <svg aria-hidden="true" className="h-2.5 w-1.5" fill="none" viewBox="0 0 6 10">
          <path
            d="M1 1L5 5L1 9"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </svg>
      </span>
    </button>
  );
}
